import type { AnalyticsObraRow, AnalyticsQuery, ObraStatus } from '@obras/shared';
import {
  behindSchedule,
  formatCnpj,
  formatCurrency,
  formatCurrencyCompact,
  formatDate,
  formatDateTime,
  formatPercent,
  OBRA_STATUS_LABELS,
  SCHEDULE_GAP_THRESHOLD,
} from '@obras/shared';
import { Link } from '@tanstack/react-router';
import { ChartLine, Download, FileText, PauseCircle, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card, CardHeader, Kpi } from '../components/ui/Card.js';
import { TextField } from '../components/ui/Field.js';
import { EmptyState, ErrorState, RefreshingBar, Skeleton } from '../components/ui/Feedback.js';
import { MultiSelect, type MultiSelectOption } from '../components/ui/MultiSelect.js';
import { MonthlyBars } from '../features/analitico/MonthlyBars.js';
import { analyticsQueryString, useAnalytics } from '../features/analitico/api.js';
import { useObraFilterOptions } from '../features/obras/api.js';
import { ApiError, api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';

const ICON = { strokeWidth: 1.5 } as const;

const STATUS_OPTIONS: readonly MultiSelectOption[] = (
  ['em_andamento', 'paralisada', 'concluida', 'cancelada'] as const satisfies readonly ObraStatus[]
).map((status) => ({ value: status, label: OBRA_STATUS_LABELS[status] }));

/** Baixa como CSV o que está em tela, com os filtros já aplicados. */
function baixarCsv(rows: readonly AnalyticsObraRow[]): void {
  const cabecalho = [
    'Obra',
    'Contrato',
    'Situação',
    'Fiscal',
    'Empresa',
    'Valor total de contratos (R$)',
    'Pago (R$)',
    'Falta (R$)',
    'Prazo decorrido (%)',
    'Feito (%)',
  ];

  /* Ponto e vírgula e vírgula decimal: é o que o Excel em pt-BR abre direto. */
  const escapar = (valor: string): string => `"${valor.replace(/"/g, '""')}"`;
  const numero = (centavos: number): string => (centavos / 100).toFixed(2).replace('.', ',');
  const pontos = (fracao: number): string => (fracao * 100).toFixed(1).replace('.', ',');

  const linhas = rows.map((row) =>
    [
      escapar(row.name),
      escapar(row.contractNumber ?? ''),
      escapar(OBRA_STATUS_LABELS[row.status]),
      escapar(row.fiscalName ?? ''),
      escapar(row.companyName ?? ''),
      numero(row.contractedCents),
      numero(row.paidCents),
      numero(Math.max(0, row.contractedCents - row.paidCents)),
      pontos(row.timeProgress),
      pontos(row.executionProgress),
    ].join(';'),
  );

  /*
   * BOM na frente: sem ele o Excel abre "Execução" como "ExecuÃ§Ã£o". Escrito
   * como escape e não como caractere literal — invisível no código, ele some
   * numa cópia distraída e o defeito volta sem deixar rastro no diff.
   */
  const conteudo = `\uFEFF${[cabecalho.join(';'), ...linhas].join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));

  const link = document.createElement('a');
  link.href = url;
  link.download = `obras-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Barra dupla: prazo decorrido atrás, execução financeira na frente.
 *
 * A leitura é a distância entre as duas — escura menor que cinza é obra andando
 * mais devagar que o calendário. É a mesma linguagem do cartão de obras, de
 * propósito: quem aprendeu lá não reaprende aqui.
 */
function ProgressPair({ row }: { row: AnalyticsObraRow }) {
  return (
    <div
      className="relative h-1 w-full overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={`Execução ${formatPercent(row.executionProgress)}, prazo decorrido ${formatPercent(row.timeProgress)}`}
    >
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-foreground/25"
        style={{ width: `${row.timeProgress * 100}%` }}
      />
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-foreground"
        style={{ width: `${row.executionProgress * 100}%` }}
      />
    </div>
  );
}

/**
 * Cabeçalho que só existe no papel.
 *
 * Um relatório impresso sem dizer de que recorte saiu é um número solto: quem
 * receber a folha não tem como saber se são todas as obras ou só as de um
 * fiscal. Então o PDF abre com o órgão, o recorte por extenso e a data de
 * emissão — que é o que a tela não precisa mostrar, porque ali os filtros
 * estão à vista.
 */
function PrintHeader({
  organization,
  recorte,
}: {
  organization: string;
  recorte: readonly string[];
}) {
  return (
    <header className="mb-6 hidden print:block">
      <p className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
        {organization}
      </p>
      <h1 className="mt-1 text-[18px] font-semibold tracking-tight">
        Relatório analítico de obras
      </h1>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        {recorte.length > 0 ? recorte.join(' · ') : 'Todo o acervo'} — emitido em{' '}
        {formatDateTime(new Date().toISOString())}
      </p>
    </header>
  );
}

/**
 * Legenda das duas barras.
 *
 * O ícone é a própria marca do gráfico, em miniatura — legenda que usa um
 * símbolo diferente do que está plotado obriga a fazer a tradução de cabeça. A
 * frase que explicava isso saiu: quem lê a barra não deveria ter de ler um
 * parágrafo antes.
 */
function BarLegend() {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground">
      <li className="flex items-center gap-2">
        <span className="h-1 w-6 shrink-0 rounded-full bg-foreground/25" aria-hidden />
        Prazo decorrido
      </li>
      <li className="flex items-center gap-2">
        <span className="h-1 w-6 shrink-0 rounded-full bg-foreground" aria-hidden />
        Pago
      </li>
      <li className="flex items-center gap-2">
        <TriangleAlert {...ICON} size={13} className="shrink-0 text-danger" aria-hidden />
        Atraso: pago mais de {Math.round(SCHEDULE_GAP_THRESHOLD * 100)} pontos percentuais atrás do
        prazo decorrido
      </li>
    </ul>
  );
}

function TableCard({
  title,
  description,
  head,
  children,
}: {
  title: string;
  description: string;
  head: readonly string[];
  children: React.ReactNode;
}) {
  return (
    <Card className="print-block overflow-hidden">
      <CardHeader title={title} description={description} />
      <div className="overflow-x-auto px-5 pb-5">
        <table className="tabular w-full min-w-[520px] text-[13px]">
          <thead>
            <tr className="text-left text-muted-foreground">
              {head.map((label, index) => (
                <th key={label} className={cn('py-1.5 font-medium', index > 0 && 'text-right')}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">{children}</tbody>
        </table>
      </div>
    </Card>
  );
}

export function RelatoriosPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fiscalIds, setFiscalIds] = useState<string[]>([]);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [secretariaIds, setSecretariaIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);

  const options = useObraFilterOptions();
  const { organization } = useAuth();

  const filters = useMemo(
    () =>
      ({
        from: from || undefined,
        to: to || undefined,
        fiscalIds,
        companyIds,
        secretariaIds,
        statuses: statuses as AnalyticsQuery['statuses'],
      }) satisfies Partial<AnalyticsQuery>,
    [from, to, fiscalIds, companyIds, secretariaIds, statuses],
  );

  const { data, isPending, isFetching, isError, error, refetch } = useAnalytics(filters);

  const [baixando, setBaixando] = useState(false);
  const [erroDoPdf, setErroDoPdf] = useState<string | null>(null);

  /* O documento é montado no servidor; aqui só se pede e se guarda o arquivo. */
  async function baixarPdf(): Promise<void> {
    setBaixando(true);
    setErroDoPdf(null);

    try {
      await api.download(`/analitico/pdf${analyticsQueryString(filters)}`, 'relatorio-obras.pdf');
    } catch (causa) {
      setErroDoPdf(causa instanceof ApiError ? causa.message : 'Não foi possível gerar o PDF.');
    } finally {
      setBaixando(false);
    }
  }

  const fiscalOptions: MultiSelectOption[] = (options.data?.fiscais ?? []).map((fiscal) => ({
    value: fiscal.id,
    label: fiscal.name,
  }));

  const companyOptions: MultiSelectOption[] = (options.data?.companies ?? []).map((company) => ({
    value: company.id,
    label: company.tradeName ?? company.legalName,
    detail: company.cnpj ? formatCnpj(company.cnpj) : undefined,
    terms: company.cnpj ? [company.cnpj, formatCnpj(company.cnpj)] : [],
  }));

  /*
   * A sigla vem primeiro na etiqueta porque é por ela que se procura: quem
   * digita "SME" não digita "Secretaria Municipal de Educação".
   */
  const secretariaOptions: MultiSelectOption[] = (options.data?.secretarias ?? []).map(
    (secretaria) => ({
      value: secretaria.id,
      label: secretaria.acronym ?? secretaria.name,
      detail: secretaria.acronym ? secretaria.name : undefined,
      terms: [secretaria.name],
    }),
  );

  const ativos =
    fiscalIds.length +
    companyIds.length +
    secretariaIds.length +
    statuses.length +
    (from ? 1 : 0) +
    (to ? 1 : 0);

  /** O recorte escrito por extenso, para o cabeçalho do documento impresso. */
  const recorte = useMemo(() => {
    const nomes = (ids: readonly string[], opcoes: readonly MultiSelectOption[]): string =>
      ids.map((id) => opcoes.find((opcao) => opcao.value === id)?.label ?? id).join(', ');

    const partes: string[] = [];

    if (from || to) {
      partes.push(
        `Período: ${from ? formatDate(from) : 'início'} a ${to ? formatDate(to) : 'hoje'}`,
      );
    }
    if (fiscalIds.length) partes.push(`Fiscal: ${nomes(fiscalIds, fiscalOptions)}`);
    if (companyIds.length) partes.push(`Empresa: ${nomes(companyIds, companyOptions)}`);
    if (secretariaIds.length) {
      partes.push(`Secretaria: ${nomes(secretariaIds, secretariaOptions)}`);
    }
    if (statuses.length) partes.push(`Situação: ${nomes(statuses, STATUS_OPTIONS)}`);

    return partes;
  }, [
    from,
    to,
    fiscalIds,
    companyIds,
    secretariaIds,
    statuses,
    fiscalOptions,
    companyOptions,
    secretariaOptions,
  ]);

  return (
    <AppShell>
      <PrintHeader organization={organization?.name ?? 'Órgão'} recorte={recorte} />

      <div className="print:hidden">
        <PageHeader
          title="Relatórios"
          description="Panorama do acervo, com apuração no servidor."
          action={
            data && data.obras.length > 0 ? (
              <div className="flex gap-2">
                <Button
                  icon={<FileText {...ICON} size={16} />}
                  loading={baixando}
                  onClick={() => void baixarPdf()}
                >
                  PDF
                </Button>
                <Button
                  icon={<Download {...ICON} size={16} />}
                  onClick={() => baixarCsv(data.obras)}
                >
                  CSV
                </Button>
              </div>
            ) : null
          }
        />
      </div>

      {/* Uma linha de filtros para tudo que está abaixo — nunca por cartão. */}
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <div className="w-36">
          <TextField
            label="De"
            type="date"
            mono
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="w-36">
          <TextField
            label="Até"
            type="date"
            mono
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>

        <MultiSelect
          label="Fiscal"
          options={fiscalOptions}
          selected={fiscalIds}
          onChange={setFiscalIds}
          searchable
        />
        <MultiSelect
          label="Empresa"
          options={companyOptions}
          selected={companyIds}
          onChange={setCompanyIds}
          searchable
        />
        <MultiSelect
          label="Secretaria"
          options={secretariaOptions}
          selected={secretariaIds}
          onChange={setSecretariaIds}
          searchable
        />
        <MultiSelect
          label="Situação"
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setStatuses}
        />

        {ativos > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
              setFiscalIds([]);
              setCompanyIds([]);
              setSecretariaIds([]);
              setStatuses([]);
            }}
          >
            Limpar
          </Button>
        ) : null}
      </div>

      {erroDoPdf ? (
        <p role="alert" className="mt-3 text-[13px] text-danger print:hidden">
          {erroDoPdf}
        </p>
      ) : null}

      <div className="mt-3 mb-5 print:hidden">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <div className="flex flex-col gap-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-[118px] rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-lg" />
        </div>
      ) : data.totals.obras === 0 ? (
        <Card>
          <EmptyState
            icon={ChartLine}
            title="Nada neste recorte"
            description="Remova algum filtro ou amplie o período."
          />
        </Card>
      ) : (
        <div className={cn('flex flex-col gap-6', isFetching && 'opacity-60')}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Obras"
              value={String(data.totals.obras)}
              hint={`${formatPercent(data.totals.executionRatio)} de execução`}
            />
            <Kpi
              label="Valor total de contratos"
              value={formatCurrency(data.totals.contractedCents)}
            />
            <Kpi
              label="Pago"
              value={formatCurrency(data.totals.paidCents)}
              tone="success"
              hint={`Saldo de ${formatCurrencyCompact(data.totals.remainingCents)}`}
            />
            <Kpi
              label="Visitas vencidas"
              value={String(data.deadlines.overdueVisits)}
              tone={data.deadlines.overdueVisits > 0 ? 'danger' : 'default'}
              hint={
                data.deadlines.dueSoonVisits > 0
                  ? `${data.deadlines.dueSoonVisits} vencendo`
                  : undefined
              }
            />
          </div>

          <Card>
            <CardHeader
              title="Medições por mês"
              description="Quanto foi efetivamente pago em cada mês do recorte."
            />
            <MonthlyBars months={data.months} />
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Medido no período" value={formatCurrency(data.flow.measuredCents)} />
            <Kpi label="Aditivos" value={formatCurrency(data.flow.additionsCents)} />
            <Kpi label="Supressões" value={formatCurrency(Math.abs(data.flow.suppressionsCents))} />
            <Kpi
              label="Glosas"
              value={formatCurrency(data.flow.glossCents)}
              hint={`${data.flow.reports} relatórios publicados`}
            />
          </div>

          {data.deadlines.paused > 0 || data.deadlines.expiredContracts > 0 ? (
            <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 p-5">
              {data.deadlines.paused > 0 ? (
                <span className="flex items-center gap-2 text-[13px]">
                  <PauseCircle {...ICON} size={15} className="text-paused" aria-hidden />
                  <strong className="tabular font-medium">{data.deadlines.paused}</strong>
                  paralisada{data.deadlines.paused === 1 ? '' : 's'}
                  {data.deadlines.pausedDays > 0
                    ? ` · ${data.deadlines.pausedDays} dias acumulados`
                    : ''}
                </span>
              ) : null}

              {data.deadlines.expiredContracts > 0 ? (
                <span className="flex items-center gap-2 text-[13px]">
                  <TriangleAlert {...ICON} size={15} className="text-danger" aria-hidden />
                  <strong className="tabular font-medium">{data.deadlines.expiredContracts}</strong>
                  com prazo de contrato vencido sem conclusão
                </span>
              ) : null}
            </Card>
          ) : null}

          <Card className="print-block overflow-hidden">
            <CardHeader title="Execução física × financeira" action={<BarLegend />} />
            <div className="overflow-x-auto px-5 pb-5">
              <table className="tabular w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1.5 font-medium">Obra</th>
                    <th className="w-40 py-1.5 font-medium">Prazo × execução</th>
                    <th className="py-1.5 text-right font-medium">Valor total de contratos</th>
                    <th className="py-1.5 text-right font-medium">Pago</th>
                    <th className="py-1.5 text-right font-medium">Falta</th>
                    <th
                      className="py-1.5 text-right font-medium"
                      title="Quanto do contrato já foi pago. A obra é marcada em atraso quando o pago fica mais de 15 pontos percentuais atrás do prazo já decorrido. A lista começa pelas mais atrasadas."
                    >
                      Feito
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.obras.map((row) => (
                    <tr key={row.id}>
                      <td className="max-w-72 py-2">
                        <Link
                          to="/obras/$obraId"
                          params={{ obraId: row.id }}
                          className="block truncate font-medium transition-colors hover:text-primary-strong"
                        >
                          {row.name}
                        </Link>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {row.fiscalName ?? 'sem fiscal'}
                          {row.companyName ? ` · ${row.companyName}` : ''}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <ProgressPair row={row} />
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrencyCompact(row.contractedCents)}
                      </td>
                      <td className="py-2 text-right">{formatCurrencyCompact(row.paidCents)}</td>
                      <td className="py-2 text-right text-muted-foreground">
                        {formatCurrencyCompact(Math.max(0, row.contractedCents - row.paidCents))}
                      </td>
                      {/*
                        A comparação que a barra ao lado mostra, dita em número —
                        e nomeada. Só a porcentagem em vermelho obriga quem lê a
                        deduzir o motivo da cor; dizer "prazo decorrido 85%" conta
                        de quanto é a defasagem sem precisar comparar colunas.
                      */}
                      <td className="py-2 text-right">
                        {behindSchedule(row.gap) ? (
                          <>
                            <span className="font-medium text-danger">
                              {formatPercent(row.executionProgress, 0)}
                            </span>
                            <span className="flex items-center justify-end gap-1 text-[11px] font-medium text-danger">
                              <TriangleAlert {...ICON} size={11} aria-hidden />
                              prazo decorrido {formatPercent(row.timeProgress, 0)}
                            </span>
                          </>
                        ) : (
                          <span className="font-medium">
                            {formatPercent(row.executionProgress, 0)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            {/*
              O fiscal não é parte contratada: é o servidor designado para
              fiscalizar a execução do contrato. Daí "Por fiscal de contrato" no
              título — e a coluna de dinheiro dizendo o que ela é, o valor dos
              contratos, e não algo que sugira que o fiscal foi contratado.

              O mesmo rótulo em toda tela e no PDF, de propósito: três nomes
              diferentes para o mesmo número obrigam quem lê a conferir se são
              mesmo a mesma coisa.
            */}
            <TableCard
              title="Por fiscal de contrato"
              description="Obras sob fiscalização de cada servidor designado, no período."
              head={[
                'Fiscal responsável',
                'Obras',
                'Valor total de contratos',
                'Pago',
                'Relatórios',
                'Vencidas',
              ]}
            >
              {data.fiscais.map((row) => (
                <tr key={row.id}>
                  <td className="max-w-40 truncate py-2">{row.name}</td>
                  <td className="py-2 text-right">{row.obras}</td>
                  <td className="py-2 text-right">{formatCurrencyCompact(row.contractedCents)}</td>
                  <td className="py-2 text-right">{formatCurrencyCompact(row.paidCents)}</td>
                  <td className="py-2 text-right">{row.reports}</td>
                  <td
                    className={cn(
                      'py-2 text-right',
                      row.overdueVisits > 0 ? 'font-medium text-danger' : 'text-muted-foreground',
                    )}
                  >
                    {row.overdueVisits}
                  </td>
                </tr>
              ))}
            </TableCard>

            <TableCard
              title="Por empresa contratada"
              description="Valor dos contratos e o que já foi faturado no período."
              head={['Empresa', 'Obras', 'Valor total de contratos', 'Faturado', 'Glosas']}
            >
              {data.companies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-3 text-muted-foreground">
                    Nenhuma obra do recorte tem empresa vinculada.
                  </td>
                </tr>
              ) : (
                data.companies.map((row) => (
                  <tr key={row.id}>
                    <td className="max-w-40 truncate py-2">{row.name}</td>
                    <td className="py-2 text-right">{row.obras}</td>
                    <td className="py-2 text-right">
                      {formatCurrencyCompact(row.contractedCents)}
                    </td>
                    <td className="py-2 text-right">{formatCurrencyCompact(row.measuredCents)}</td>
                    <td className="py-2 text-right text-muted-foreground">
                      {formatCurrencyCompact(row.glossCents)}
                    </td>
                  </tr>
                ))
              )}
            </TableCard>
          </div>
        </div>
      )}
    </AppShell>
  );
}
